// Function to get product data from the existing productJson script tag
function getProductData() {
	try {
		const productJsonElement = document.querySelector('.productJson');
		if (productJsonElement) {
			const productData = JSON.parse(productJsonElement.textContent);
			return productData;
		}

		// Alternative: try to find product JSON by other selectors
		const productJsonAlt = document.querySelector('[data-product-json]');
		if (productJsonAlt) {
			const productData = JSON.parse(productJsonAlt.textContent);
			return productData;
		}

		return null;
	} catch (error) {
		console.error('Error parsing product JSON:', error);
		return null;
	}
}

// Try to get product data from JSON
const productData = getProductData();

// Check if we're in the main product section context
const productSection = document.querySelector('[id^="MainProduct-"]');

// Cache utility functions
class ReviewsCache {
	constructor(config = {}) {
		this.CACHE_PREFIX = 'judgeme_reviews_';

		// Use configurable cache durations from JudgeMeConfig
		const cacheConfig = window.JudgeMeConfig?.cache || {};
		this.enabled = cacheConfig.enabled !== false; // Default to enabled
		this.debug = cacheConfig.debug || false;

		this.CACHE_DURATION = {
			reviews: cacheConfig.duration?.reviews || 24 * 60 * 60 * 1000, // 24 hours default
			summary: cacheConfig.duration?.summary || 48 * 60 * 60 * 1000, // 48 hours default
		};
	}

	// Generate cache key
	getCacheKey(productId, page = 1, perPage = 10, type = 'reviews') {
		return `${this.CACHE_PREFIX}${type}_${productId}_p${page}_pp${perPage}`;
	}

	// Check if cache is valid
	isCacheValid(timestamp, durationType = 'reviews') {
		if (!timestamp) return false;
		const now = Date.now();
		const duration =
			this.CACHE_DURATION[durationType] || this.CACHE_DURATION.reviews;
		const isValid = now - timestamp < duration;

		return isValid;
	}

	// Get cached data
	get(productId, page = 1, perPage = 10, type = 'reviews') {
		if (!this.enabled) {
			return null;
		}

		try {
			const cacheKey = this.getCacheKey(productId, page, perPage, type);
			const cachedData = localStorage.getItem(cacheKey);

			if (!cachedData) {
				return null;
			}

			const parsed = JSON.parse(cachedData);

			// Validate that the cached productId matches the requested productId
			if (parsed.productId !== productId) {
				console.warn(
					`Cache productId mismatch: cached=${parsed.productId}, requested=${productId}`
				);
				localStorage.removeItem(cacheKey);
				return null;
			}

			// Validate that page and perPage match
			if (parsed.page !== page || parsed.perPage !== perPage) {
				console.warn(`Cache pagination mismatch for product ${productId}`);
				localStorage.removeItem(cacheKey);
				return null;
			}

			if (!this.isCacheValid(parsed.timestamp, type)) {
				localStorage.removeItem(cacheKey);
				return null;
			}

			return parsed.data;
		} catch (error) {
			console.error('Error reading from cache:', error);
			return null;
		}
	}

	// Set cached data
	set(productId, data, page = 1, perPage = 10, type = 'reviews') {
		if (!this.enabled) {
			return;
		}

		try {
			const cacheKey = this.getCacheKey(productId, page, perPage, type);
			const cacheData = {
				data: data,
				timestamp: Date.now(),
				productId: productId,
				page: page,
				perPage: perPage,
				type: type,
			};

			localStorage.setItem(cacheKey, JSON.stringify(cacheData));
		} catch (error) {
			console.error('Error writing to cache:', error);
			// Handle quota exceeded or other localStorage errors
			this.clearOldCache();
		}
	}

	// Clear old cache entries
	clearOldCache() {
		if (!this.enabled) return;

		try {
			const keys = Object.keys(localStorage);
			const now = Date.now();
			let clearedCount = 0;

			keys.forEach((key) => {
				if (key.startsWith(this.CACHE_PREFIX)) {
					try {
						const cachedData = JSON.parse(localStorage.getItem(key));
						const maxDuration = Math.max(...Object.values(this.CACHE_DURATION));

						if (
							!this.isCacheValid(cachedData.timestamp) ||
							now - cachedData.timestamp > maxDuration
						) {
							localStorage.removeItem(key);
							clearedCount++;
						}
					} catch (e) {
						// Invalid cache entry, remove it
						localStorage.removeItem(key);
						clearedCount++;
					}
				}
			});
		} catch (error) {
			console.error('Error clearing old cache:', error);
		}
	}

	// Clear all reviews cache (useful for debugging)
	clearAll() {
		try {
			const keys = Object.keys(localStorage);
			let clearedCount = 0;

			keys.forEach((key) => {
				if (key.startsWith(this.CACHE_PREFIX)) {
					localStorage.removeItem(key);
					clearedCount++;
				}
			});
		} catch (error) {
			console.error('Error clearing cache:', error);
		}
	}

	// Get cache statistics
	getStats() {
		try {
			const keys = Object.keys(localStorage);
			const reviewsKeys = keys.filter((key) =>
				key.startsWith(this.CACHE_PREFIX)
			);
			const stats = {
				enabled: this.enabled,
				totalEntries: reviewsKeys.length,
				totalSize: 0,
				validEntries: 0,
				expiredEntries: 0,
				cacheDurations: this.CACHE_DURATION,
			};

			reviewsKeys.forEach((key) => {
				const data = localStorage.getItem(key);
				stats.totalSize += data.length;

				try {
					const parsed = JSON.parse(data);
					if (this.isCacheValid(parsed.timestamp)) {
						stats.validEntries++;
					} else {
						stats.expiredEntries++;
					}
				} catch (e) {
					stats.expiredEntries++;
				}
			});

			// Convert size to human readable format
			stats.totalSizeFormatted = this.formatBytes(stats.totalSize);

			return stats;
		} catch (error) {
			console.error('Error getting cache stats:', error);
			return null;
		}
	}

	// Format bytes to human readable size
	formatBytes(bytes) {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
	}

	// Force refresh - clear cache for specific product
	forceRefresh(productId) {
		try {
			const keys = Object.keys(localStorage);
			let clearedCount = 0;

			keys.forEach((key) => {
				if (
					key.startsWith(this.CACHE_PREFIX) &&
					key.includes(`_${productId}_`)
				) {
					localStorage.removeItem(key);
					clearedCount++;
				}
			});

			return clearedCount;
		} catch (error) {
			console.error('Error during force refresh:', error);
			return 0;
		}
	}
}

// Judge.me Reviews Integration
class JudgeMeReviews {
	constructor(config) {
		this.config = {
			productId: config.productId,
			perPage: config.perPage || window.JudgeMeConfig?.defaultPerPage || 10,
			...config,
		};
		this.currentPage = 1;
		this.reviews = [];
		this.reviewsData = null;
		this.lang = window.JudgeMeConfig?.lang || {};
		this.cache = new ReviewsCache();
		this.showAllReviews = false; // Track if showing all reviews or just first 4

		// Store global reference for debugging
		window.judgeMeReviewsInstance = this;

		this.init();
	}

	init() {
		const container = document.querySelector('.judgeme-reviews-container');
		if (container) {
			// Clear old cache on initialization
			this.cache.clearOldCache();
			this.fetchReviews();
		}
	}

	// Generate test data for debug mode
	generateTestData(page = 1, perPage = 10) {
		const testReviews = [
			{
				id: 1,
				title: 'Amazing Product Quality!',
				body: "I absolutely love this product! The quality exceeded my expectations and it arrived exactly as described. The craftsmanship is top-notch and it's been working perfectly for months now. Would definitely recommend to anyone looking for a reliable option.",
				rating: 5,
				created_at: '2024-01-15T10:30:00Z',
				reviewer: {
					name: 'Sarah Johnson',
				},
			},
			{
				id: 2,
				title: 'Good value for money',
				body: 'Solid product overall. Works as advertised and the price point is very reasonable. Setup was straightforward and customer service was helpful when I had questions. Minor issues with packaging but the product itself is great.',
				rating: 4,
				created_at: '2024-01-10T14:22:00Z',
				reviewer: {
					name: 'Mike Chen',
				},
			},
			{
				id: 3,
				title: 'Perfect for my needs',
				body: 'Exactly what I was looking for! Fits perfectly in my space and does everything I need it to do. The design is sleek and modern, and it integrates well with my existing setup.',
				rating: 5,
				created_at: '2024-01-08T09:15:00Z',
				reviewer: {
					name: 'Emily Rodriguez',
				},
			},
			{
				id: 4,
				title: 'Could be better',
				body: "It's okay but has some room for improvement. The main functionality works fine but there are a few minor issues that make it less than perfect. Still usable and does the job, just not exceptional.",
				rating: 3,
				created_at: '2024-01-05T16:45:00Z',
				reviewer: {
					name: 'David Wilson',
				},
			},
			{
				id: 5,
				title: 'Outstanding customer service',
				body: 'Had an issue with my initial order and the customer service team went above and beyond to make it right. The product quality is excellent and the company clearly cares about their customers. Five stars!',
				rating: 5,
				created_at: '2024-01-03T11:20:00Z',
				reviewer: {
					name: 'Lisa Thompson',
				},
			},
			{
				id: 6,
				title: 'Decent but not spectacular',
				body: "It works fine for basic use but doesn't have all the features I was hoping for. Build quality is decent and it serves its purpose. For the price, it's acceptable but there are probably better options out there.",
				rating: 3,
				created_at: '2023-12-28T13:30:00Z',
				reviewer: {
					name: 'Robert Kim',
				},
			},
			{
				id: 7,
				title: 'Highly recommended!',
				body: "This is my second purchase from this brand and I'm consistently impressed. Great attention to detail, durable construction, and it performs exactly as promised. Will definitely be buying again.",
				rating: 5,
				created_at: '2023-12-25T08:45:00Z',
				reviewer: {
					name: 'Jennifer Adams',
				},
			},
			{
				id: 8,
				title: 'Fast shipping and great product',
				body: 'Ordered on Monday and received it Wednesday. Product was well-packaged and in perfect condition. Works great and looks even better than the photos. Very satisfied with this purchase.',
				rating: 4,
				created_at: '2023-12-22T17:10:00Z',
				reviewer: {
					name: 'Mark Davis',
				},
			},
			{
				id: 9,
				title: 'Not quite what I expected',
				body: "The product is functional but doesn't quite match what I was expecting based on the description. It's not bad, just different than anticipated. Could use clearer product details.",
				rating: 3,
				created_at: '2023-12-20T12:05:00Z',
				reviewer: {
					name: 'Amanda White',
				},
			},
			{
				id: 10,
				title: 'Excellent quality and design',
				body: 'Beautiful product with exceptional build quality. The design is modern and sophisticated, and it works flawlessly. A bit pricey but you definitely get what you pay for. Very happy with this purchase and would buy again.',
				rating: 5,
				created_at: '2023-12-18T15:25:00Z',
				reviewer: {
					name: 'James Martinez',
				},
			},
			{
				id: 11,
				title: 'Impressive durability',
				body: 'Been using this for over a year now and it still looks and works like new. The materials are clearly high quality and built to last. Great investment for long-term use.',
				rating: 5,
				created_at: '2023-12-15T11:30:00Z',
				reviewer: {
					name: "Kelly O'Brien",
				},
			},
			{
				id: 12,
				title: 'Average experience',
				body: 'Nothing special but does what it promises. The delivery was on time and the product matches the description. Would consider other options next time though.',
				rating: 3,
				created_at: '2023-12-12T14:20:00Z',
				reviewer: {
					name: 'Thomas Anderson',
				},
			},
			{
				id: 13,
				title: 'Great for beginners',
				body: 'Perfect starter option for anyone new to this type of product. Easy to use, clear instructions, and good support resources. Exactly what I needed to get started.',
				rating: 4,
				created_at: '2023-12-10T16:45:00Z',
				reviewer: {
					name: 'Rachel Green',
				},
			},
			{
				id: 14,
				title: 'Exceeded expectations',
				body: "Honestly didn't expect much at this price point, but this product has blown me away. Quality is exceptional and it performs better than products costing twice as much. Highly recommend!",
				rating: 5,
				created_at: '2023-12-08T09:15:00Z',
				reviewer: {
					name: 'Alex Turner',
				},
			},
			{
				id: 15,
				title: 'Stylish and functional',
				body: 'Love the design aesthetic - it looks great in my space and works perfectly. The attention to detail in both form and function is impressive. Worth every penny.',
				rating: 5,
				created_at: '2023-12-05T13:40:00Z',
				reviewer: {
					name: 'Maya Patel',
				},
			},
			{
				id: 16,
				title: 'Had some issues initially',
				body: "Started with a few problems but customer service sorted everything out quickly. Product works great now and I'm satisfied with the resolution. Good follow-up support.",
				rating: 4,
				created_at: '2023-12-03T10:25:00Z',
				reviewer: {
					name: 'Chris Taylor',
				},
			},
			{
				id: 17,
				title: 'Not worth the price',
				body: "Expected more for what I paid. It works but feels cheaply made and doesn't justify the cost. There are better alternatives available for similar or less money.",
				rating: 2,
				created_at: '2023-11-30T15:50:00Z',
				reviewer: {
					name: 'Jordan Smith',
				},
			},
			{
				id: 18,
				title: 'Perfect replacement',
				body: 'My old one finally gave out after years of use, and this was the perfect replacement. Same quality I expected and works just as well. Very happy with the purchase.',
				rating: 4,
				created_at: '2023-11-28T12:10:00Z',
				reviewer: {
					name: 'Patricia Williams',
				},
			},
			{
				id: 19,
				title: 'Innovative design',
				body: "Love the thoughtful design elements that make this stand out from competitors. It's clear a lot of thought went into the user experience. Modern and practical.",
				rating: 5,
				created_at: '2023-11-25T08:30:00Z',
				reviewer: {
					name: 'Sam Rodriguez',
				},
			},
			{
				id: 20,
				title: 'Solid everyday use',
				body: "Use this daily and it holds up well to regular wear and tear. Reliable performance and haven't had any issues. Good for consistent, everyday use.",
				rating: 4,
				created_at: '2023-11-22T14:15:00Z',
				reviewer: {
					name: 'Nicole Johnson',
				},
			},
			{
				id: 21,
				title: 'Could use improvements',
				body: 'Does the job but there are several areas where it could be better. The concept is good but the execution leaves something to be desired. Not bad, just not great.',
				rating: 3,
				created_at: '2023-11-20T11:45:00Z',
				reviewer: {
					name: 'Brian Miller',
				},
			},
			{
				id: 22,
				title: 'Fantastic quality control',
				body: 'You can tell this was made with care and attention to detail. Every aspect feels premium and well-thought-out. The quality control is clearly excellent.',
				rating: 5,
				created_at: '2023-11-18T16:20:00Z',
				reviewer: {
					name: 'Hannah Davis',
				},
			},
			{
				id: 23,
				title: 'Great customer support',
				body: 'Had a question about setup and the support team was incredibly helpful and responsive. The product itself is good, but the service really sets them apart.',
				rating: 4,
				created_at: '2023-11-15T13:55:00Z',
				reviewer: {
					name: 'Ryan Thompson',
				},
			},
			{
				id: 24,
				title: 'Mixed feelings',
				body: "Some aspects are really good while others are disappointing. It's functional and does what it's supposed to do, but I expected more consistency across all features.",
				rating: 3,
				created_at: '2023-11-12T10:30:00Z',
				reviewer: {
					name: 'Stephanie Lee',
				},
			},
			{
				id: 25,
				title: 'Outstanding value',
				body: 'For the price, this is an absolute steal. The quality is much higher than I expected and it performs as well as products costing significantly more. Great value proposition.',
				rating: 5,
				created_at: '2023-11-10T15:40:00Z',
				reviewer: {
					name: 'Marcus Brown',
				},
			},
			{
				id: 26,
				title: 'Easy to use',
				body: 'Setup was straightforward and using it is intuitive. No complicated instructions or confusing features. Just works as expected right out of the box.',
				rating: 4,
				created_at: '2023-11-08T09:20:00Z',
				reviewer: {
					name: 'Jessica Garcia',
				},
			},
			{
				id: 27,
				title: 'Disappointed',
				body: "This didn't meet my expectations at all. The quality feels poor and it doesn't work as well as advertised. Would not recommend based on my experience.",
				rating: 2,
				created_at: '2023-11-05T14:10:00Z',
				reviewer: {
					name: 'Kevin Wilson',
				},
			},
			{
				id: 28,
				title: 'Reliable performance',
				body: "Consistent and dependable performance every time I use it. No surprises or issues - just steady, reliable functionality. That's exactly what I was looking for.",
				rating: 4,
				created_at: '2023-11-03T12:25:00Z',
				reviewer: {
					name: 'Laura Anderson',
				},
			},
			{
				id: 29,
				title: 'Premium feel',
				body: 'This feels like a premium product from the moment you unbox it. The materials, finish, and overall build quality all scream high-end. Worth the investment.',
				rating: 5,
				created_at: '2023-11-01T11:50:00Z',
				reviewer: {
					name: 'Daniel Martinez',
				},
			},
			{
				id: 30,
				title: 'Good but not great',
				body: "It's a decent product that does what it's supposed to do. Nothing exceptional but nothing terrible either. Meets basic needs without any standout features.",
				rating: 3,
				created_at: '2023-10-29T16:35:00Z',
				reviewer: {
					name: 'Ashley Clark',
				},
			},
		];

		// Calculate pagination
		const startIndex = (page - 1) * perPage;
		const endIndex = startIndex + perPage;
		const paginatedReviews = testReviews.slice(startIndex, endIndex);

		return {
			reviews: paginatedReviews,
			total_reviews: testReviews.length,
			current_page: page,
			per_page: perPage,
			total_pages: Math.ceil(testReviews.length / perPage),
		};
	}

	async fetchReviews(page = 1) {
		// Check if debug mode is enabled
		const isDebugMode = window.JudgeMeConfig?.cache?.debug === true;

		if (isDebugMode) {
			console.log('🐛 DEBUG MODE: Using test data instead of API');

			// Simulate API delay for realistic testing
			await new Promise((resolve) => setTimeout(resolve, 500));

			const testData = this.generateTestData(page, this.config.perPage);

			this.reviewsData = testData;
			this.currentPage = page;

			this.renderReviewsSummary(testData);
			this.renderReviews(testData.reviews);
			return;
		}

		// Try to get from cache first
		const cachedData = this.cache.get(
			this.config.productId,
			page,
			this.config.perPage
		);

		if (cachedData) {
			this.reviewsData = cachedData;
			this.currentPage = page;

			this.renderReviewsSummary(cachedData);
			this.renderReviews(cachedData.reviews);
			return;
		}

		try {
			// Use Cloudflare Worker if available, otherwise fallback to direct API
			const useWorker = window.JudgeMeConfig?.workerEndpoint;

			if (useWorker) {
				const workerUrl = `${window.JudgeMeConfig.workerEndpoint}/reviews?productId=${this.config.productId}&page=${page}&perPage=${this.config.perPage}`;

				console.log('workerUrl', workerUrl);
				const response = await fetch(workerUrl, {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
				});

				if (!response.ok) {
					throw new Error(`Worker API error! status: ${response.status}`);
				}

				const data = await response.json();

				console.log('data', data);

				// Cache the response
				this.cache.set(this.config.productId, data, page, this.config.perPage);

				this.reviewsData = data;
				this.currentPage = page;

				this.renderReviewsSummary(data);
				this.renderReviews(data.reviews);
			} else {
				// Fallback: Instead of calling the API, show no reviews
				const data = {
					reviews: [],
					total_reviews: 0,
					current_page: page,
					per_page: this.config.perPage,
				};

				this.reviewsData = data;
				this.currentPage = page;

				this.renderReviewsSummary(data);
				this.renderReviews(data.reviews);
			}
		} catch (error) {
			console.error('Error fetching Judge.me reviews:', error);

			// Instead of showing error, show no reviews
			const data = {
				reviews: [],
				total_reviews: 0,
				current_page: 1,
				per_page: this.config.perPage,
			};

			this.reviewsData = data;
			this.currentPage = 1;

			this.renderReviewsSummary(data);
			this.renderReviews(data.reviews);
		}
	}

	renderReviewsSummary(data) {
		// Find the reviews heading element and append rating info
		const reviewsHeading = document.querySelector('.reviews-heading');
		if (reviewsHeading && data.reviews && data.reviews.length > 0) {
			const averageRating = this.calculateAverageRating(data.reviews);
			const totalReviews = data.reviews.length;

			// Remove any existing rating info to avoid duplicates
			const existingRatingInfo = reviewsHeading.querySelector(
				'.rating-info-append'
			);
			if (existingRatingInfo) {
				existingRatingInfo.remove();
			}

			// Create and append the rating info
			const ratingInfoElement = document.createElement('div');
			ratingInfoElement.className =
				'rating-info-append d-flex align-center gap-5 ml-15';
			const starUrl = window.JudgeMeConfig?.starSvgUrl || '/cdn/shop/assets/star.svg';
			ratingInfoElement.innerHTML = `
				<img src="${starUrl}" class="star star-full mb-2" width="10" height="10" alt="Star rating">
				<span class="heading-color uppercase letter-spacing-1 fs-small-3">${averageRating.toFixed(
					1
				)}</span>
				<span class="heading-color uppercase letter-spacing-1 fs-small-3" style="color: #8c8c8c;">(${totalReviews})</span>
			`;

			reviewsHeading.appendChild(ratingInfoElement);

			// remove the `judgeme-reviews-summary`
			const summaryContainer = document.getElementById(
				'judgeme-reviews-summary'
			);
			if (summaryContainer) {
				summaryContainer.remove();
			}
		} else {
			const summaryContainer = document.getElementById(
				'judgeme-reviews-summary'
			);
			if (!summaryContainer) {
				return;
			}

			if (!data.reviews || data.reviews.length === 0) {
				summaryContainer.innerHTML = `
          <div class="no-reviews py-20">
            <p>${this.lang.noReviews || 'No reviews yet.'}</p>
          </div>
        `;
			}
		}
	}

	renderReviews(reviews) {
		const reviewsContainer = document.getElementById('judgeme-reviews-list');
		if (!reviewsContainer || !reviews) {
			return;
		}

		if (reviews.length === 0) {
			reviewsContainer.innerHTML = '';
			return;
		}

		// Show only first 4 reviews unless showAllReviews is true
		const reviewsToShow = this.showAllReviews ? reviews : reviews.slice(0, 4);
		const reviewLength = reviews.length;

		const reviewsHTML = reviewsToShow
			.map((review) => this.renderSingleReview(review, reviewLength))
			.join('');
		reviewsContainer.innerHTML = reviewsHTML;

		// Add "SEE ALL REVIEWS" button if there are more than 4 reviews and not showing all
		const seeAllButton =
			reviews.length > 4 && !this.showAllReviews
				? `<button class="btn btn-outline see-all-reviews-btn" id="see-all-reviews-btn">
					<span class="heading-color uppercase letter-spacing-1 fs-small-3">SEE ALL REVIEWS</span>
				</button>`
				: '';

		const seeAllContainer = document.getElementById('judgeme-reviews-see-all');

		if (seeAllContainer) {
			seeAllContainer.innerHTML = seeAllButton;
		}

		// Add event listener for "SEE ALL REVIEWS" button
		const seeAllBtn = document.getElementById('see-all-reviews-btn');
		if (seeAllBtn) {
			seeAllBtn.addEventListener('click', () => {
				this.openReviewsModal();
			});
		}

		// Initialize truncation for all review bodies
		this.initializeTruncation();
	}

	renderSingleReview(review, reviewLength) {
		const reviewDate = new Date(review.created_at).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});

		// Generate unique ID for this review's truncation
		const reviewId = `review-${Math.random().toString(36).substr(2, 9)}`;

		return `
      <div class="single-review py-30 ${
				reviewLength > 2 ? 'border-bottom-important' : ''
			}">
        <div class="review-header d-flex align-center space-between mb-15">
        <div class="review-title">
          <span class="heading-color uppercase letter-spacing-1 fs-small-3">${
						review.title
					}</span>
        </div>
          <div class="review-rating">
            ${this.renderStars(review.rating)}
          </div>
        </div>
        <div class="review-content">
          <div class="review-body-container my-16" data-review-id="${reviewId}">
            <h6 class="fs-bigger-2 my-0 review-body-text">${
							review.body || ''
						}</h6>
            <span class="read-more-toggle heading-color uppercase letter-spacing-1 fs-small-3" style="color: #8c8c8c; cursor: pointer; display: none;">READ MORE</span>
          </div>
        </div>
        <div class="review-date"></div>
          <span class="text-color uppercase letter-spacing-1 fs-small-3">${reviewDate}</span>
        </div>
      </div>
    `;
	}

	initializeTruncation() {
		const reviewBodies = document.querySelectorAll('.review-body-container');

		reviewBodies.forEach((container) => {
			const textElement = container.querySelector('.review-body-text');
			const toggleElement = container.querySelector('.read-more-toggle');
			const fullText = textElement.textContent;
			const wordLimit = 20; // Adjust as needed

			if (fullText.split(' ').length > wordLimit) {
				const truncatedText =
					fullText.split(' ').slice(0, wordLimit).join(' ') + '...';
				let isExpanded = false;

				// Set initial truncated text
				textElement.textContent = truncatedText;
				toggleElement.style.display = 'inline';
				toggleElement.textContent = 'READ MORE';

				// Add click event
				toggleElement.addEventListener('click', () => {
					if (isExpanded) {
						textElement.textContent = truncatedText;
						toggleElement.textContent = 'READ MORE';
						isExpanded = false;
					} else {
						textElement.textContent = fullText;
						toggleElement.textContent = 'SEE LESS';
						isExpanded = true;
					}
				});
			}
		});
	}

	renderStars(rating) {
		const fullStars = Math.floor(rating);
		const hasHalfStar = rating % 1 !== 0;
		const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
		const starUrl = window.JudgeMeConfig?.starSvgUrl || '/cdn/shop/assets/star.svg';

		let starsHTML = '';

		for (let i = 0; i < fullStars; i++) {
			starsHTML += `<img src="${starUrl}" class="star star-full" width="12" height="12" alt="Full star">`;
		}

		if (hasHalfStar) {
			starsHTML += `<img src="${starUrl}" class="star star-half" width="12" height="12" style="opacity: 0.5;" alt="Half star">`;
		}

		for (let i = 0; i < emptyStars; i++) {
			starsHTML += `<img src="${starUrl}" class="star star-empty" width="12" height="12" style="opacity: 0.3;" alt="Empty star">`;
		}

		return `<div class="stars-container d-flex align-center gap-2">${starsHTML}</div>`;
	}

	calculateAverageRating(reviews) {
		if (!reviews || reviews.length === 0) return 0;
		const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
		return sum / reviews.length;
	}

	renderError() {
		const summaryContainer = document.getElementById('judgeme-reviews-summary');
		const reviewsContainer = document.getElementById('judgeme-reviews-list');

		if (summaryContainer) {
			summaryContainer.innerHTML = `<div class="error-message">${
				this.lang.loadingError || 'Unable to load reviews at this time.'
			}</div>`;
		}

		if (reviewsContainer) {
			reviewsContainer.innerHTML = '';
		}
	}

	openReviewsModal() {
		this.createModalHTML();
		this.loadAllReviewsInModal();
		this.setupModalEventListeners();

		// Show modal with animation - use requestAnimationFrame to ensure initial state is rendered
		const modal = document.getElementById('judgeme-reviews-modal');
		if (modal) {
			document.body.style.overflow = 'hidden'; // Prevent background scroll

			// Force a reflow to ensure the modal is rendered in its initial state
			modal.offsetHeight;

			// Use requestAnimationFrame to add the show class in the next frame
			requestAnimationFrame(() => {
				modal.classList.add('show');
			});
		}
	}

	createModalHTML() {
		// Remove existing modal if it exists
		const existingModal = document.getElementById('judgeme-reviews-modal');
		if (existingModal) {
			existingModal.remove();
		}

		const modalHTML = `
			<div id="judgeme-reviews-modal" class="judgeme-reviews-modal">
				<div class="judgeme-modal-overlay"></div>
				<div class="judgeme-modal-content">
					<div class="judgeme-modal-header">
						<span class="heading-color uppercase letter-spacing-1 fs-small">Reviews</span>
						<close-button class="judgeme-modal-close no-js-hidden hover-svg-zoom pointer" tabindex="0">
							<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<line x1="18" y1="6" x2="6" y2="18"></line>
								<line x1="6" y1="6" x2="18" y2="18"></line>
							</svg>
						</close-button>
					</div>
					<div class="judgeme-modal-body">
						<div id="judgeme-modal-reviews-list" class="modal-reviews-list">
							<div class="loading-reviews">Loading all reviews...</div>
						</div>
						<div id="judgeme-modal-loading" class="modal-loading-indicator" style="display: none;">
							<div class="loading-spinner"></div>
						</div>
						<div id="judgeme-modal-end" class="modal-end-indicator" style="display: none;">
							<p class="text-color uppercase letter-spacing-1 fs-small-3">End</p>
						</div>
					</div>
				</div>
			</div>
		`;

		document.body.insertAdjacentHTML('beforeend', modalHTML);
	}

	async loadAllReviewsInModal(page = 1) {
		const modal = document.getElementById('judgeme-reviews-modal');
		if (!modal) return;

		const reviewsList = document.getElementById('judgeme-modal-reviews-list');
		const loadingIndicator = document.getElementById('judgeme-modal-loading');
		const endIndicator = document.getElementById('judgeme-modal-end');

		if (page === 1) {
			reviewsList.innerHTML =
				'<div class="loading-reviews">Loading all reviews...</div>';
		} else {
			loadingIndicator.style.display = 'flex';
		}

		try {
			// Check if debug mode is enabled
			const isDebugMode = window.JudgeMeConfig?.cache?.debug === true;

			let data;
			if (isDebugMode) {
				// Simulate API delay for realistic testing
				await new Promise((resolve) => setTimeout(resolve, 500));
				data = this.generateTestData(page, 10);
			} else {
				// Try to get from cache first
				const cachedData = this.cache.get(this.config.productId, page, 10);
				if (cachedData) {
					data = cachedData;
				} else {
					// Use Cloudflare Worker if available, otherwise fallback to direct API
					const useWorker = window.JudgeMeConfig?.workerEndpoint;

					if (useWorker) {
						const workerUrl = `${window.JudgeMeConfig.workerEndpoint}/reviews?productId=${this.config.productId}&page=${page}&perPage=10`;

						const response = await fetch(workerUrl, {
							method: 'GET',
							headers: {
								'Content-Type': 'application/json',
							},
						});

						if (!response.ok) {
							throw new Error(`Worker API error! status: ${response.status}`);
						}

						data = await response.json();
						this.cache.set(this.config.productId, data, page, 10);
					} else {
						// Fallback: show no more reviews
						data = {
							reviews: [],
							total_reviews: 0,
							current_page: page,
							per_page: 10,
							total_pages: 0,
						};
					}
				}
			}

			// Hide loading indicators
			loadingIndicator.style.display = 'none';

			if (page === 1) {
				reviewsList.innerHTML = '';
				this.modalCurrentPage = 1;
				this.modalHasMoreReviews = true;
			}

			if (data.reviews && data.reviews.length > 0) {
				const reviewsHTML = data.reviews
					.map((review) => this.renderSingleModalReview(review))
					.join('');
				reviewsList.insertAdjacentHTML('beforeend', reviewsHTML);

				// Check if there are more pages
				if (page >= (data.total_pages || 1) || data.reviews.length < 10) {
					this.modalHasMoreReviews = false;
					endIndicator.style.display = 'block';
				}
			} else {
				if (page === 1) {
					reviewsList.innerHTML =
						'<div class="no-reviews py-20"><p>No reviews yet.</p></div>';
				}
				this.modalHasMoreReviews = false;
				endIndicator.style.display = 'block';
			}

			// Initialize truncation for new reviews
			this.initializeModalTruncation();
		} catch (error) {
			console.error('Error loading reviews in modal:', error);
			loadingIndicator.style.display = 'none';

			if (page === 1) {
				reviewsList.innerHTML =
					'<div class="error-message">Unable to load reviews at this time.</div>';
			}
			this.modalHasMoreReviews = false;
		}
	}

	renderSingleModalReview(review) {
		const reviewDate = new Date(review.created_at).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});

		// Generate unique ID for this review's truncation
		const reviewId = `modal-review-${Math.random().toString(36).substr(2, 9)}`;

		return `
			<div class="single-modal-review py-30">
				<div class="review-header d-flex align-center space-between mb-15">
					<div class="review-title">
						<span class="heading-color uppercase letter-spacing-1 fs-small-3">${
							review.title
						}</span>
					</div>
					<div class="review-rating">
						${this.renderStars(review.rating)}
					</div>
				</div>
				<div class="review-content">
					<div class="review-body-container my-16" data-review-id="${reviewId}">
						<h6 class="fs-bigger-2 my-0 review-body-text">${review.body || ''}</h6>
						<span class="read-more-toggle heading-color uppercase letter-spacing-1 fs-small-3" style="color: #8c8c8c; cursor: pointer; display: none;">READ MORE</span>
					</div>
				</div>
				<div class="review-date">
					<span class="text-color uppercase letter-spacing-1 fs-small-3">${reviewDate}</span>
				</div>
			</div>
		`;
	}

	initializeModalTruncation() {
		const reviewBodies = document.querySelectorAll(
			'#judgeme-modal-reviews-list .review-body-container'
		);

		reviewBodies.forEach((container) => {
			// Skip if already initialized
			if (container.hasAttribute('data-truncation-initialized')) return;
			container.setAttribute('data-truncation-initialized', 'true');

			const textElement = container.querySelector('.review-body-text');
			const toggleElement = container.querySelector('.read-more-toggle');
			const fullText = textElement.textContent;
			const wordLimit = 20;

			if (fullText.split(' ').length > wordLimit) {
				const truncatedText =
					fullText.split(' ').slice(0, wordLimit).join(' ') + '...';
				let isExpanded = false;

				// Set initial truncated text
				textElement.textContent = truncatedText;
				toggleElement.style.display = 'inline';
				toggleElement.textContent = 'READ MORE';

				// Add click event
				toggleElement.addEventListener('click', () => {
					if (isExpanded) {
						textElement.textContent = truncatedText;
						toggleElement.textContent = 'READ MORE';
						isExpanded = false;
					} else {
						textElement.textContent = fullText;
						toggleElement.textContent = 'SEE LESS';
						isExpanded = true;
					}
				});
			}
		});
	}

	setupModalEventListeners() {
		const modal = document.getElementById('judgeme-reviews-modal');
		const overlay = modal.querySelector('.judgeme-modal-overlay');
		const closeBtn = modal.querySelector('.judgeme-modal-close');
		const modalBody = modal.querySelector('.judgeme-modal-body');

		// Initialize modal state
		this.modalCurrentPage = 1;
		this.modalHasMoreReviews = true;
		this.modalIsLoading = false;

		// Close modal on overlay click
		overlay.addEventListener('click', () => {
			this.closeModal();
		});

		// Close modal on close button click
		closeBtn.addEventListener('click', () => {
			this.closeModal();
		});

		// Close modal on Escape key
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && modal.classList.contains('show')) {
				this.closeModal();
			}
		});

		// Infinite scroll
		modalBody.addEventListener('scroll', () => {
			if (this.modalIsLoading || !this.modalHasMoreReviews) return;

			const scrollTop = modalBody.scrollTop;
			const scrollHeight = modalBody.scrollHeight;
			const clientHeight = modalBody.clientHeight;

			// Load more when scrolled to 90% of the content
			if (scrollTop + clientHeight >= scrollHeight * 0.9) {
				this.modalIsLoading = true;
				this.modalCurrentPage++;
				this.loadAllReviewsInModal(this.modalCurrentPage).finally(() => {
					this.modalIsLoading = false;
				});
			}
		});
	}

	closeModal() {
		const modal = document.getElementById('judgeme-reviews-modal');
		if (modal) {
			modal.classList.remove('show');
			document.body.style.overflow = ''; // Restore background scroll

			// Remove modal after animation completes (400ms to match CSS transition)
			setTimeout(() => {
				if (modal.parentNode) {
					modal.remove();
				}
			}, 400);
		}
	}
}

// Initialize Judge.me Reviews when DOM is loaded
document.addEventListener('DOMContentLoaded', function () {
	const reviewsContainer = document.querySelector('.judgeme-reviews-container');

	if (reviewsContainer) {
		// Get product data from the productJson script tag
		const productData = getProductData();

		if (productData && productData.id) {
			// Wait for config to load
			const initReviews = () => {
				if (window.JudgeMeConfig) {
					const config = {
						productId: productData.id,
						perPage: 10, // You can get this from block settings if needed
					};

					new JudgeMeReviews(config);
				} else {
					// Retry after a short delay if config isn't loaded yet
					setTimeout(initReviews, 100);
				}
			};

			initReviews();
		} else {
			// Maybe the productJson hasn't loaded yet, let's retry
			setTimeout(() => {
				const retryProductData = getProductData();
				if (retryProductData && retryProductData.id) {
					const config = {
						productId: retryProductData.id,
						perPage: 10,
					};

					if (window.JudgeMeConfig) {
						new JudgeMeReviews(config);
					} else {
					}
				} else {
				}
			}, 500);
		}
	}
});

// Global utility functions for cache management and debugging
// These can be called from browser console for debugging
window.JudgeMeUtils = {
	// Get cache statistics
	getCacheStats: function () {
		if (window.judgeMeReviewsInstance?.cache) {
			const stats = window.judgeMeReviewsInstance.cache.getStats();
			console.table(stats);
			return stats;
		} else {
			return null;
		}
	},

	// Clear all cache
	clearCache: function () {
		if (window.judgeMeReviewsInstance?.cache) {
			window.judgeMeReviewsInstance.cache.clearAll();
		} else {
		}
	},

	// Force refresh reviews for current product
	forceRefresh: function () {
		if (window.judgeMeReviewsInstance) {
			const productId = window.judgeMeReviewsInstance.config.productId;
			const clearedCount =
				window.judgeMeReviewsInstance.cache.forceRefresh(productId);

			// Refresh the reviews
			window.judgeMeReviewsInstance.fetchReviews(1);
		} else {
		}
	},

	// Enable/disable cache debugging
	toggleDebug: function (enable = null) {
		if (window.judgeMeReviewsInstance?.cache) {
			if (enable === null) {
				window.judgeMeReviewsInstance.cache.debug =
					!window.judgeMeReviewsInstance.cache.debug;
			} else {
				window.judgeMeReviewsInstance.cache.debug = enable;
			}
		} else {
		}
	},

	// Get configuration
	getConfig: function () {
		if (window.judgeMeReviewsInstance) {
			return {
				instance: window.judgeMeReviewsInstance.config,
				cache: window.JudgeMeConfig?.cache,
			};
		} else {
			return null;
		}
	},
};

// Store reference to the reviews instance globally for debugging
window.judgeMeReviewsInstance = null;
